import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { List } from '@bb/elements-web';
import { ListItem } from '@brokerbay/design-components';
import { paramaterize } from 'utils';
import { Badge, Empty, Modal, Text, Tooltip } from 'components';
import { orgs as searchOrgs } from 'api/SearchController';
import SearchBar from '../SearchBar';
import { useMLSStaffContext } from '../MLSStaffContext';

type AddUserModalProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

export const assume = (orgId: string, userId?: string) => {
  const chatSessionStorageKeys = [
    'messageDrafts',
    'messageDraftsForNewConvo',
    'chatInstances',
    'activeChatInstance',
    'newConvoSelections',
    'assumedChatInstances',
    'assumedBrokerageId',
  ];

  chatSessionStorageKeys.forEach(key => sessionStorage.removeItem(key));
  window.location.href = `/answering/assume/${orgId}?${paramaterize({
    redirectPath: userId ? `/users/edit/${userId}` : '/users/new',
  })}`;
};

const AddUserModal: React.FC<AddUserModalProps> = ({ visible, setVisible }) => {
  const [searchResults, setSearchResults] = useState([]);
  const { modalSearchText: debouncedText, loading } = useMLSStaffContext();

  const search = async () => {
    if (debouncedText) {
      const results = await searchOrgs({
        input: debouncedText,
        features: ['SUPRA_MVP'],
      });
      setSearchResults(results.orgs);
    } else {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    search();
  }, [debouncedText]);

  const sortOids = (oids: string[]) => {
    if (oids.length > 5) {
      const matches: string[] = [];
      const rest: string[] = [];
      oids.forEach(oid => {
        if (oid.toLowerCase().includes(debouncedText.toLowerCase())) {
          matches.push(oid);
        } else {
          rest.push(oid);
        }
      });
      const updatedOids = [...matches, ...rest];
      const first5 = updatedOids.slice(0, 5);
      const count = updatedOids.length - 5;
      return (
        <StyledText>
          OID: {first5.join(', ')}{' '}
          <Tooltip title={`OID: ${updatedOids.slice(5).join(', ')}`}>
            <Badge color="primary" count={`+${count}`} />
          </Tooltip>
        </StyledText>
      );
    }
    return <StyledText>OID: {oids.join(', ')}</StyledText>;
  };

  const buildBrokerageListItem = (b: any) => (
    <StyledListItem key={b.id} onClick={() => assume(b.id)}>
      <Text>{b.name}</Text>
      {sortOids(b.oids)}
    </StyledListItem>
  );

  const getDescription = () => (
    <Empty
      image="../../../../img/empty.png"
      imageStyle={{ height: 200 }}
      description="Search for a brokerage or OID to begin"
    />
  );

  const getNoResults = () => (
    <Empty
      image="../../../../img/default.png"
      imageStyle={{ height: 200 }}
      description="There are no brokerages found with that name or OID"
    />
  );

  return (
    <StyledModal
      title="Find a brokerage"
      open={visible}
      onCancel={() => {
        setVisible(false);
      }}
      footer={null}
    >
      <StyledTopContainer>
        <StyledText>
          Select which brokerage you want to assume and add a new user to.
        </StyledText>
      </StyledTopContainer>
      <SearchBar type="modal" />
      <StyledBoldText>Choose a brokerage</StyledBoldText>
      <StyledList
        dataCy="add-user-search-results"
        dataSource={searchResults}
        renderItem={item => buildBrokerageListItem(item)}
        locale={{
          emptyText: debouncedText ? getNoResults() : getDescription(),
        }}
      />
    </StyledModal>
  );
};

export default AddUserModal;

const StyledModal = styled(Modal)`
  height: 60vh;
  min-height: 425px;ß
`;

const StyledTopContainer = styled.div`
  margin-bottom: 8px;
`;

const StyledText = styled(Text)`
  display: block;
  color: rgba(0, 0, 0, 0.65);
`;

const StyledBoldText = styled(Text)`
  display: block;
  margin: 16px 0px;
  font-weight: 600;
`;
const StyledList = styled(List)`
  .ant5-divider {
    display: none;
  }
  .ant-list-item:hover {
    background-color: #cdcdcd;
    cursor: pointer;
  }
  height: 45vh;
  overflow-y: scroll;
`;

const StyledListItem = styled(ListItem)`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;
  border: 1px solid #cdcdcd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
`;
